/*!
 * useLive.js — use() from the live console: a drawing from blocks/ on the stage
 * © 2026 Rob Canning — GPLv3
 *
 * In a score, use(path:…) is a placeholder that a block fills when the score
 * loads (preProcessReuse.js). Run from the console there is no placeholder:
 * the block is put on the screen where the performer is looking, as shape()
 * puts a polygon, and is named, so the rest of the set can reach it.
 *
 *     use(orion)                              blocks/…/orion.svg, wherever it is
 *     use(src:constellations/orion)           that one file
 *     use(path:constellations, index:12)      the 13th file of a folder
 *     use(orion, size:0.6, at:[0.3, 0.5])     how big, and where (as shape())
 *     use(orion) rotate(dur:20, loop:0)       what follows acts on it
 *     orion > scale(values:[1, 1.4], dur:8)   later, by name
 *     use(orion, stroke:#ff2a6d, fill:#fff)   its lines and its insides
 *     use(uid:orion, remove:1)                take it away; use(remove:all) every one
 *
 * The name is the uid: written, else the file's name -- use(orion) is orion
 * -- else the folder's, so use(path:constellations, index:rand(0, 87)) run
 * again swaps the picture rather than adding one (useUid, cueUtils.js).
 *
 * The file is inlined, not shown through <image>: what is inside is part of
 * the score, so stroke:, fill: and width: recolour its lines and fills, and
 * every id in it is reachable with the block's name in front -- orion_lines,
 * ursaminor_polaris -- for o2p(path:orion_lines), follow(), another rotate.
 *
 * The element is <g id=uid class="oscilla-shape oscilla-block"> around one
 * nested <svg> sized to the box, so it scrolls, zooms, turns and is taken
 * away exactly as a made shape is (shape.js removeShape). The group is made
 * at once and the file fills it when it has loaded, so the cues after it on
 * the line have something to act on straight away.
 */

import { scoreSvg, shapesGroup, placement, sizePx, removeShape } from "./shape.js";
import { syncLiveOverflow } from "../system/liveOverflow.js";
import { publish } from "../control/paramBinding.js";
import { evalMaybeRandom } from "./audio/audioShared.js";
import { resolveFolderIndex } from "../parser/preProcessReuse.js";
import { useUid } from "../parser/cueUtils.js";
import { atPair } from "../system/viewBand.js";
import { projectName } from "../system/projectContext.js";

const SVG_NS = "http://www.w3.org/2000/svg";
const DEFAULT_SIZE = 0.5;
const INKED = "path, line, polyline, polygon, circle, ellipse, rect, text, use";

const fileCache = new Map();     // blocks/ path -> Promise<root element | null>
let names = null;                // Promise<Map name -> blocks/ path>
let namesNow = [];               // the same names, for the console's suggestions

const blocksUrl = rel => `/${window.projectBase || `scores/${projectName()}/`}blocks/` +
  rel.split("/").map(encodeURIComponent).join("/");

async function listDir(rel) {
  if (!projectName()) return { files: [], dirs: [] };     // no project, no blocks/
  const url = `/api/block-list/${encodeURIComponent(projectName())}` +
    (rel ? "/" + rel.split("/").map(encodeURIComponent).join("/") : "");
  try {
    const r = await fetch(url, { cache: "no-store" });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const d = await r.json();
    return { files: d.files || [], dirs: d.directories || [] };
  } catch (err) {
    console.warn(`[use] could not list blocks/${rel}: ${err.message}`);
    return { files: [], dirs: [] };
  }
}

/** Every block by its name: blocks/ first, then its folders, a few deep. */
function blockNames(fresh = false) {
  if (names && !fresh) return names;
  names = (async () => {
    const map = new Map();
    let level = [""];
    for (let depth = 0; depth < 4 && level.length; depth++) {
      const next = [];
      for (const dir of level) {
        const { files, dirs } = await listDir(dir);
        for (const f of files) {
          const n = f.replace(/\.svg$/i, "").toLowerCase();
          if (!map.has(n)) map.set(n, dir ? `${dir}/${f}` : f);
        }
        for (const s of dirs) next.push(dir ? `${dir}/${s}` : s);
      }
      level = next;
    }
    namesNow = [...map.keys()].sort();
    return map;
  })();
  return names;
}

/** The block names known so far, for suggestions; the first call starts the listing. */
export function knownBlocks() {
  if (!names) blockNames();
  return namesNow;
}

function loadFile(rel) {
  if (!fileCache.has(rel)) {
    fileCache.set(rel, fetch(blocksUrl(rel), { cache: "no-store" })
      .then(r => (r.ok ? r.text() : null))
      .then(text => {
        if (text == null) return null;
        const doc = new DOMParser().parseFromString(text, "image/svg+xml");
        return doc.querySelector("parsererror") ? null : doc.documentElement;
      })
      .catch(() => null)
      .then(root => { if (!root) fileCache.delete(rel); return root; }));
  }
  return fileCache.get(rel);
}

/** Which file, as a path under blocks/, or null. */
async function resolveFile(src, path, index) {
  if (src != null) {
    const s = String(src).replace(/^\/+/, "");
    if (s.includes("/")) return /\.svg$/i.test(s) ? s : `${s}.svg`;
    const n = s.replace(/\.svg$/i, "").toLowerCase();
    return (await blockNames()).get(n) ?? (await blockNames(true)).get(n) ?? null;
  }
  const folder = String(path).replace(/^\/+|\/+$/g, "");
  const { files } = await listDir(folder);
  if (!files.length) return null;
  const v = index == null || typeof index === "number" || index?.type === "pattern"
    ? index : Number(evalMaybeRandom(index));
  const i = resolveFolderIndex(v, files.length, `live|${folder}|${JSON.stringify(index ?? null)}`);
  return `${folder}/${files[i]}`;
}

/** The file's drawing into the block's <svg>, its ids under the block's name. */
function fillBody(el, body, root) {
  body.replaceChildren();
  const w = parseFloat(root.getAttribute("width")) || 100;
  const h = parseFloat(root.getAttribute("height")) || 100;
  body.setAttribute("viewBox", root.getAttribute("viewBox") || `0 0 ${w} ${h}`);
  for (const n of root.childNodes) {
    if (n.nodeName === "title") continue;            // no tooltip over the stage
    body.appendChild(document.importNode(n, true));
  }
  const ids = new Map();
  for (const n of body.querySelectorAll("[id]")) {
    const id = `${el.id}_${n.id}`;
    ids.set(n.id, id);
    n.id = id;
  }
  if (ids.size) {
    for (const n of body.querySelectorAll("*")) {
      for (const a of [...n.attributes]) {
        if (!a.value.includes("#")) continue;
        let v = a.value.replace(/url\(#([^)]+)\)/g, (m, id) => (ids.has(id) ? `url(#${ids.get(id)})` : m));
        if (v.startsWith("#") && ids.has(v.slice(1))) v = `#${ids.get(v.slice(1))}`;
        if (v !== a.value) n.setAttribute(a.name, v);
      }
    }
  }
  // What has a line and what has an inside, read once, as drawn: a later
  // stroke: then colours only what was stroked.
  for (const n of body.querySelectorAll(INKED)) {
    const cs = getComputedStyle(n);
    n._ink = { stroke: cs.stroke !== "none", fill: cs.fill !== "none" };
  }
}

/** stroke:, fill:, width: as last asked, on whatever is drawn now. */
function applyLook(el) {
  const body = el.querySelector("svg.oscilla-block-body");
  if (!body) return;
  const d = el.dataset;
  for (const n of body.querySelectorAll(INKED)) {
    if (!n._ink) continue;
    if (n._ink.stroke) {
      n.style.stroke = d.stroke || "";
      n.style.strokeWidth = d.width || "";
    }
    if (n._ink.fill) n.style.fill = d.fill || "";
  }
}

function word(v) {
  const r = evalMaybeRandom(v);
  return r == null ? null : String(r);
}

function num(v, fallback) {
  const r = Number(evalMaybeRandom(v));
  return Number.isFinite(r) ? r : fallback;
}

/**
 * use(...) from the console: make or change a block on the stage. Returns
 * its element at once, so the cues after it on the line can act on it.
 */
export function handleUseLive(ast) {
  const args = ast?.args || [];
  const arg = name => { const v = args.find(a => a.type === name)?.value; return name === "at" ? atPair(v) : v; };   // at:0.5 is [0.5, 0.5]
  const svg = scoreSvg();
  if (!svg) { console.warn("[use] no score to draw into"); return null; }

  const remove = arg("remove");
  if (remove != null) {
    if (String(remove) === "all") {
      for (const el of [...svg.querySelectorAll(".oscilla-block")]) removeShape(el);
    } else {
      const el = document.getElementById(String(useUid(args) ?? ""));
      if (el?.classList.contains("oscilla-block")) removeShape(el);
    }
    syncLiveOverflow(svg);
    return null;
  }

  const uid = useUid(args);
  if (!uid) { console.warn("[use] which one? use(orion), use(src:folder/file) or use(path:folder, index:2)"); return null; }
  let el = document.getElementById(uid);
  if (el && !el.classList.contains("oscilla-block")) {
    console.warn(`[use] uid:${uid} is already an element of the score — pick another name`);
    return null;
  }

  const group = shapesGroup(svg);
  let made = false;
  if (!el) {
    el = document.createElementNS(SVG_NS, "g");
    el.id = uid;
    el.classList.add("oscilla-shape", "oscilla-block");
    const body = document.createElementNS(SVG_NS, "svg");
    body.classList.add("oscilla-block-body");
    body.setAttribute("overflow", "visible");
    body.setAttribute("preserveAspectRatio", "xMidYMid meet");
    el.appendChild(body);
    group.appendChild(el);
    made = true;
  }
  // an animation may have wrapped the body in its own group inside ours
  const body = el.querySelector("svg.oscilla-block-body");

  // Where and how big: as shape() -- kept from the last run unless asked again
  const d = el.dataset;
  const at = arg("at");
  if (at != null || d.cx == null) {
    const p = placement(group, at);
    if (!p) { if (made) el.remove(); return null; }
    d.cx = p.cx; d.cy = p.cy; d.perPx = p.perPx;
    d.fx = Array.isArray(at) ? num(at[0], 0.5) : 0.5;
    d.fy = Array.isArray(at) ? num(at[1], 0.5) : 0.5;
  }
  publish("shape", uid, { x: Number(d.fx ?? 0.5), y: Number(d.fy ?? 0.5) });
  const sz = arg("size");
  let size = d.size != null ? JSON.parse(d.size) : DEFAULT_SIZE;
  if (Array.isArray(sz)) size = [num(sz[0], DEFAULT_SIZE), num(sz[1] ?? sz[0], DEFAULT_SIZE)];
  else if (sz != null) size = num(sz, DEFAULT_SIZE);
  d.size = JSON.stringify(size);
  const [wPx, hPx] = sizePx(size);
  const w = wPx * Number(d.perPx), h = hPx * Number(d.perPx);
  body.setAttribute("x", Number(d.cx) - w / 2);
  body.setAttribute("y", Number(d.cy) - h / 2);
  body.setAttribute("width", w);
  body.setAttribute("height", h);
  // turn about the box's centre; a rotate() already running read the old one
  el.setAttribute("data-pivot", `${d.cx},${d.cy}`);
  for (const n of [el, ...el.querySelectorAll("*")]) {
    if (n.style?.transformOrigin) n.style.transformOrigin = `${d.cx}px ${d.cy}px`;
  }

  // the look: color: is lines and insides both
  const color = word(arg("color"));
  const stroke = word(arg("stroke")) ?? color;
  const fill = word(arg("fill")) ?? color;
  if (stroke != null) d.stroke = stroke;
  if (fill != null) d.fill = fill;
  if (arg("width") != null) d.width = String(num(arg("width"), 1));
  if (arg("opacity") != null) el.setAttribute("opacity", String(num(arg("opacity"), 1)));
  applyLook(el);
  if (made) syncLiveOverflow(svg);

  // what it shows: only when a file is named (viz() changes the look alone)
  const src = arg("src"), path = arg("path");
  if (src != null || path != null) {
    const token = (el._useToken = (el._useToken || 0) + 1);
    const srcWord = src != null ? word(src) : null;
    resolveFile(srcWord, path, arg("index")).then(async rel => {
      if (el._useToken !== token || !el.isConnected) return;
      if (!rel) {
        console.warn(`[use] no block "${srcWord ?? path}" in blocks/ — ${src != null ? "a file name without .svg, or folder/file" : "an empty folder?"}`);
        if (!d.file) removeShape(el);
        return;
      }
      if (d.file === rel) return;                     // already showing it
      const root = await loadFile(rel);
      if (el._useToken !== token || !el.isConnected) return;
      if (!root) {
        console.warn(`[use] could not read blocks/${rel}`);
        if (!d.file) removeShape(el);
        return;
      }
      fillBody(el, body, root);
      d.file = rel;
      applyLook(el);
    });
  }
  return el;
}
